import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { isAuthenticated, getUser, canEditProducts } from './utils/auth';
import Login from './pages/Login';
import AdminDashboard from './pages/admin/Dashboard';
import Products from './pages/admin/Products';
import RetailPlans from './pages/admin/RetailPlans';
import StockStatus from './pages/admin/StockStatus';
import WastageDashboard from './pages/admin/WastageDashboard';
import Procurement from './pages/admin/Procurement';
import QuickCommerce from './pages/admin/QuickCommerce';
import RetailerOrders from './pages/admin/RetailerOrders';
import Wastage from './pages/admin/Wastage';
import VariableExpenses from './pages/admin/VariableExpenses';
import FixedExpenses from './pages/admin/FixedExpenses';
import Cashflow from './pages/admin/Cashflow';
import LaborCosts from './pages/admin/LaborCosts';
import UserManagement from './pages/admin/UserManagement';
import Backup from './pages/admin/Backup';
import DuplicateInvoiceReport from './pages/admin/DuplicateInvoiceReport';
import RetailerDashboard from './pages/retailer/Dashboard';
import StaffDashboard from './pages/staff/Dashboard';
import StaffAttendance from './pages/staff/Attendance';
import FieldTeamDashboard from './pages/field_team/Dashboard';
import TicketsList from './pages/tickets/TicketsList';
import TicketDetail from './pages/tickets/TicketDetail';
import TeamDashboard from './pages/tickets/TeamDashboard';
import { initCompanySettings } from './config/companySettings';
import './App.css';

function ProtectedRoute({ children, allowedRoles, check }) {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  const user = getUser();
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }
  // Optional per-route predicate for finer-grained allowlists (e.g. the
  // Products tab is role=admin + a named staff allowlist). The predicate
  // runs AFTER allowedRoles has already passed, so pages can keep using
  // the plain allowedRoles prop when no extra check is needed.
  if (typeof check === 'function' && !check(user)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  // Fire the single-source brand/contact fetch at app boot so every document
  // generator (invoice, PO, receipt, QC, wastage, PDFs, share text) reads a
  // consistent value from the backend's central settings endpoint.
  React.useEffect(() => { initCompanySettings(); }, []);
  const user = getUser();

  const getDefaultRoute = () => {
    if (!isAuthenticated()) return '/login';
    
    switch (user?.role) {
      case 'admin':
        return '/admin/dashboard';
      case 'staff':
        return '/admin/stock-status'; // Staff lands on Stock Status (no P&L access)
      case 'retailer':
        return '/retailer/dashboard';
      case 'field_team':
        return '/field-team/dashboard';
      default:
        return '/login';
    }
  };

  return (
    <BrowserRouter>
      <div className="App">
        <Routes>
          <Route path="/login" element={<Login />} />
          
          <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
          
          {/* Admin Only Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <ProtectedRoute allowedRoles={['admin', 'staff']} check={canEditProducts}>
                <Products />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/retail-plans"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <RetailPlans />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/stock-status"
            element={
              <ProtectedRoute allowedRoles={['admin', 'staff']}>
                <StockStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/wastage-dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin', 'staff']}>
                <WastageDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/procurement"
            element={
              <ProtectedRoute allowedRoles={['admin', 'staff']}>
                <Procurement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/quick-commerce"
            element={
              <ProtectedRoute allowedRoles={['admin', 'staff']}>
                <QuickCommerce />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/retailer-orders"
            element={
              <ProtectedRoute allowedRoles={['admin', 'staff']}>
                <RetailerOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/duplicate-invoice-report"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <DuplicateInvoiceReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/wastage"
            element={
              <ProtectedRoute allowedRoles={['admin', 'staff']}>
                <Wastage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/variable-expenses"
            element={
              <ProtectedRoute allowedRoles={['admin', 'staff']}>
                <VariableExpenses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/fixed-expenses"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <FixedExpenses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cashflow"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Cashflow />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/labor-costs"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <LaborCosts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/backup"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Backup />
              </ProtectedRoute>
            }
          />
          
          {/* Retailer Routes */}
          <Route
            path="/retailer/dashboard"
            element={
              <ProtectedRoute allowedRoles={['retailer']}>
                <RetailerDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Staff Routes */}
          <Route
            path="/staff/dashboard"
            element={
              <ProtectedRoute allowedRoles={['staff']}>
                <StaffDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/staff/attendance"
            element={
              <ProtectedRoute allowedRoles={['staff', 'admin']}>
                <StaffAttendance />
              </ProtectedRoute>
            }
          />
          
          {/* Field Team Routes */}
          <Route
            path="/field-team/dashboard"
            element={
              <ProtectedRoute allowedRoles={['field_team']}>
                <FieldTeamDashboard />
              </ProtectedRoute>
            }
          />

          {/* Tickets - shared across admin/staff/field_team */}
          <Route
            path="/tickets"
            element={
              <ProtectedRoute allowedRoles={['admin', 'staff', 'field_team']}>
                <TicketsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets/team"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <TeamDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'staff', 'field_team']}>
                <TicketDetail />
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster position="top-right" />
      </div>
    </BrowserRouter>
  );
}

export default App;